<script setup>
import { computed, watch, ref } from 'vue';
import { useStore } from 'vuex';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { messageStamp } from 'shared/helpers/timeHelper';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import BubbleImage from 'dashboard/components/widgets/conversation/bubble/Image.vue';

const { id } = defineProps({
  id: {
    type: Number,
    default: null,
  },
});

const store = useStore();

// 方法，用于获取订单详情
const fetchOrderDetails = async order_id => {
  await store.dispatch('giftCards/getOrderDetails', {
    id: order_id,
  });
};

// 从 Vuex 中获取状态和详情数据
const uiFlags = useMapGetter('giftCards/getUIFlags');
const orderGet = useMapGetter('giftCards/getOrderDetails');
const fetchingOrderDetails = computed(() => uiFlags.value.fetchingOrderDetails);
const orderDetails = computed(() => orderGet.value);
const formattedDate = time => {
  return time > 0 ? messageStamp(time, 'Y LLL d, h:mm a') : '--';
};

const statusMap = {
  待处理: { text: 'pending', color: 'text-orange-600 bg-orange-100' },
  成功: { text: 'success', color: 'text-teal-600 bg-teal-100' },
  失败: { text: 'failed', color: 'text-red-600 bg-red-100' },
  部分成功: { text: 'success', color: 'text-teal-600 bg-teal-100' },
};

// 计算属性：状态颜色
const statusTextColor = computed(() => {
  return statusMap[orderDetails.value.orderInfo.status]?.color;
});

// 计算属性：状态文字
const statusText = computed(() => {
  return statusMap[orderDetails.value.orderInfo.status]?.text;
});

const paymentStatusMap = {
  1: { text: 'pending', color: 'text-orange-600 bg-orange-100' },
  2: { text: 'pending', color: 'text-orange-600 bg-orange-100' },
  3: { text: 'success', color: 'text-teal-600 bg-teal-100' },
  4: { text: 'failed', color: 'text-red-600 bg-red-100' },
};

const paymentStatusTextColor = computed(() => {
  return paymentStatusMap[orderDetails.value.withdrawInfo.status]?.color;
});

// 计算属性：状态文字
const paymentStatusText = computed(() => {
  return paymentStatusMap[orderDetails.value.withdrawInfo.status]?.text;
});

// ID变化时加载数据
watch(
  () => id,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      fetchOrderDetails(newVal);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-if="fetchingOrderDetails"
    class="flex items-center justify-center h-40"
  >
    <Spinner />
  </div>

  <div
    v-if="!fetchingOrderDetails && orderDetails.orderInfo !== undefined"
    class=""
  >
    <div class="bg-white rounded mb-3">
      <CardLayout>
        <div class="">
          <h3 class="text-lg font-medium text-ash-900">Order Information</h3>
          <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div class="flex justify-between">
              <span class="text-gray-500">Order No:</span>
              <span>{{ orderDetails.orderInfo.order_no }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Status:</span>
              <span :class="statusTextColor" class="px-2 py-0.5 rounded-full">
                {{ statusText }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Creation Time:</span>
              <span>{{
                formattedDate(orderDetails.orderInfo.create_time)
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Completion Time:</span>
              <span>
                {{ formattedDate(orderDetails.orderInfo.deal_end_time) }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Country:</span>
              <span>{{ orderDetails.orderInfo.country_name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Amount:</span>
              <span>
                <span class="font-medium">
                  {{ orderDetails.orderInfo.country_symbol }}
                </span>
                {{ orderDetails.orderInfo.purchase_amount }}
              </span>
            </div>
          </div>
        </div>
      </CardLayout>

      <!-- Payment Information -->
      <CardLayout class="mt-2" v-if="orderDetails.withdrawInfo">
        <div class="">
          <h3 class="text-lg font-medium text-ash-900">Payment Information</h3>
          <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div class="flex justify-between">
              <span class="text-gray-500">Withdraw Number:</span>
              <span>{{ orderDetails.withdrawInfo.withdraw_no }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">User Bank:</span>
              <span>{{ orderDetails.withdrawInfo.user_bank }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Account Name:</span>
              <span>{{ orderDetails.withdrawInfo.account_name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Account Number:</span>
              <span>{{ orderDetails.withdrawInfo.account_no }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Amount:</span>
              <span>{{ orderDetails.withdrawInfo.amount }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Creation Time:</span>
              <span>{{
                  formattedDate(orderDetails.withdrawInfo.create_time)
                }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Completion Time:</span>
              <span>{{
                  formattedDate(
                    orderDetails.withdrawInfo.deal_time ||
                    orderDetails.withdrawInfo.system_deal_time
                  )
                }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Status:</span>
              <span
                :class="paymentStatusTextColor"
                class="px-2 py-0.5 rounded-full"
              >
                {{ paymentStatusText }}
              </span>
            </div>
          </div>
        </div>
      </CardLayout>

      <!-- Card Information -->
      <CardLayout class="mt-2">
        <div class="">
          <h3 class="text-lg font-medium text-ash-900 mb-4">Card Information</h3>

          <!-- Cards Grid -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div
              v-for="cdkey in orderDetails.cdkeys"
              :key="cdkey.id"
              class="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow duration-200"
            >
              <!-- Card Header -->
              <div class="flex justify-between items-center">
                <div class="text-xl text-gray-800">
                  {{ cdkey.card_info.category }}
                </div>
                <div class="text-lg text-blue-600">
                  {{ cdkey.card_info.currency_symbol }} {{ cdkey.facevalue }}
                </div>
              </div>

              <!-- Card Content -->
              <div class="space-y-4">
                <div class="grid grid-cols-12 text-base">
                  <span class="col-span-3 text-gray-500">CDKey:</span>
                  <span class="col-span-9 font-mono">{{ cdkey.cdkey }}</span>
                </div>

                <div
                  v-if="cdkey.purchase_status === 2"
                  class="grid grid-cols-12 text-base"
                >
                  <span class="col-span-3 text-gray-500">Rate:</span>
                  <span class="col-span-9">{{ cdkey.purchase_rate }}</span>
                </div>

                <div
                  v-if="cdkey.purchase_status === 2"
                  class="grid grid-cols-12 text-base">
                  <span class="col-span-3 text-gray-500">Amount:</span>
                  <span class="col-span-9">{{ cdkey.purchase_amount }}</span>
                </div>

                <!-- Failure Information -->
                <div
                  v-if="cdkey.purchase_status === 3"
                  class="mt-6 pt-4 border-t border-gray-200">
                  <div class="text-base text-red-600 mb-3">
                    <span class="font-medium">Failure Reason: </span>
                    {{ cdkey.sellout_memo || 'No failure reason' }}
                  </div>
                  <div class="flex justify-center">
                    <BubbleImage
                      :url="cdkey.sellout_image"
                      class="cursor-pointer w-full max-w-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardLayout>
      <!-- Order Images -->
      <CardLayout class="mt-2">
        <div class="">
          <h3 class="text-lg font-medium text-ash-900">Order Images</h3>
          <div v-if="orderDetails.imgarr.length" class="grid grid-cols-4 gap-2">
            <div
              v-for="img in orderDetails.imgarr"
              :key="img.id"
              class="relative w-full h-24 sm:h-32 md:h-40 rounded-lg overflow-hidden shadow hover:shadow-lg transition-shadow"
            >
              <BubbleImage :url="img.img_url" class="cursor-pointer" />
            </div>
          </div>
          <p v-else class="text-xs text-gray-500">No images available.</p>
        </div>
      </CardLayout>
    </div>
  </div>
</template>

<style scoped></style>
